import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { PostCard } from './PostCard';
import { PageCard } from './PageCard';
import type { Post, KontentinoPage, PostAssignments } from '../types';

interface BulkAssignmentProps {
  posts: Post[];
  selectedPages: KontentinoPage[];
  postAssignments: PostAssignments;
  onAssignPosts: (assignments: PostAssignments) => void;
  onBack: () => void;
  onNext: () => void;
}

export function BulkAssignment({
  posts,
  selectedPages,
  postAssignments,
  onAssignPosts,
  onBack,
  onNext,
}: BulkAssignmentProps) {
  const handleTogglePost = (pageId: string, postId: string) => {
    const currentPagePosts = postAssignments[pageId] || [];
    const newPagePosts = currentPagePosts.includes(postId)
      ? currentPagePosts.filter((id) => id !== postId)
      : [...currentPagePosts, postId];

    onAssignPosts({
      ...postAssignments,
      [pageId]: newPagePosts,
    });
  };

  const handleSelectAllForPage = (pageId: string) => {
    const page = selectedPages.find((p) => p.id === pageId);
    if (!page) return;

    // Get all posts matching this platform
    const platformPosts = posts
      .filter((post) => post.platform === page.platform)
      .map((post) => post.id);

    onAssignPosts({
      ...postAssignments,
      [pageId]: platformPosts,
    });
  };

  const handleClearPage = (pageId: string) => {
    onAssignPosts({
      ...postAssignments,
      [pageId]: [],
    });
  };

  const getTotalAssignments = () => {
    return Object.values(postAssignments).reduce((sum, posts) => sum + posts.length, 0);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Assign Posts to Pages</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Select which posts to schedule for each page
          </p>
        </div>
      </div>

      {selectedPages.map((page) => {
        const pagePostIds = postAssignments[page.id] || [];
        const platformPosts = posts.filter((post) => post.platform === page.platform);
        const allPlatformPostsSelected =
          platformPosts.length > 0 &&
          platformPosts.every((post) => pagePostIds.includes(post.id));

        return (
          <div key={page.id} className="border rounded-lg p-4 space-y-3 bg-card">
            {/* Page header */}
            <div className="flex items-center justify-between">
              <PageCard page={page} />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSelectAllForPage(page.id)}
                  disabled={allPlatformPostsSelected || platformPosts.length === 0}
                >
                  Select All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleClearPage(page.id)}
                  disabled={pagePostIds.length === 0}
                >
                  Clear
                </Button>
              </div>
            </div>

            {/* Post list with checkboxes - only show platform-compatible posts */}
            <div className="space-y-2 pl-4">
              {platformPosts.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  No {page.platform} posts available
                </p>
              ) : (
                platformPosts.map((post) => {
                  const isAssigned = pagePostIds.includes(post.id);

                  return (
                    <div
                      key={post.id}
                      className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors bg-muted/30 ${
                        isAssigned
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50 hover:bg-muted/50'
                      }`}
                      onClick={() => handleTogglePost(page.id, post.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleTogglePost(page.id, post.id);
                        }
                      }}
                      aria-label={`${isAssigned ? 'Unassign' : 'Assign'} post "${post.topic || post.text.slice(0, 50)}" to ${page.name}`}
                    >
                      <Checkbox
                        checked={isAssigned}
                        className="mt-1 pointer-events-none"
                      />
                      <div className="flex-1 min-w-0">
                        <PostCard post={post} compact />
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Page summary */}
            <div className="text-sm text-muted-foreground text-right">
              {pagePostIds.length} {pagePostIds.length === 1 ? 'post' : 'posts'} assigned
            </div>
          </div>
        );
      })}

      {/* Footer actions */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button variant="outline" onClick={onBack}>
          ← Back
        </Button>
        <div className="flex items-center gap-4">
          <div className="text-sm text-muted-foreground">
            {getTotalAssignments()} total assignments
          </div>
          <Button onClick={onNext} disabled={getTotalAssignments() === 0}>
            Review Assignments →
          </Button>
        </div>
      </div>
    </div>
  );
}
